import React, {Component} from 'react';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  MessageErrorsEnum,
  MessageErrorSourcesEnum,
} from '../../../../../../../libs/shared/core/src/models/api/messageErrorModel';
import {DraftMessageModel} from '../../../../../../../libs/shared/core/src/models/api/messageModel';
import {OkCancelDialog} from '../../../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {DomMarkdownRenderer} from '../../../../../common/formatters/markdown/domMarkdownRenderer';
import {markdownDomComponents} from '../../../../../common/formatters/markdown/markdownDomComponents';
import {MessageBanner} from './messageBanner';
import {MessageBannerAction} from './messageBannerAction';
import {MessageBannerActionGroup} from './messageBannerActionGroup';
import {MessageBannerSubtitle} from './messageBannerSubtitle';
import {MessageBannerTitle} from './messageBannerTitle';

/*
 * Props.
 */

interface FailedMessageBannerProps {
  message: DraftMessageModel;
  onRetryFailedDraftClick: () => void;
  onDeleteDraftClick: () => void;
  isRounded: boolean;
}

/*
 * Component.
 */

interface FailedMessageBannerState {
  isDeleteConfirmationOpen: boolean;
}

export class FailedMessageBanner extends Component<FailedMessageBannerProps, FailedMessageBannerState> {
  constructor(props: FailedMessageBannerProps) {
    super(props);
    this.state = {isDeleteConfirmationOpen: false};
  }

  /*
   * Event handlers.
   */

  private readonly toggleIsDeleteConfirmationOpen = () => {
    this.setState(({isDeleteConfirmationOpen}) => ({isDeleteConfirmationOpen: !isDeleteConfirmationOpen}));
  };

  /*
   * Render.
   */

  render() {
    return (
      <>
        {this.renderBanner()}
        {this.maybeRenderDeleteConfirmation()}
      </>
    );
  }

  renderBanner() {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <MessageBanner
            color={theme.palette.red.shade40}
            iconName="warningFilledSmall"
            isRounded={this.props.isRounded}
          >
            <MessageBannerTitle>
              <Trans id="sendfailed-title">Failed to send</Trans>
            </MessageBannerTitle>
            {this.maybeRenderDraftError()}
            <MessageBannerActionGroup>
              <MessageBannerAction onClick={this.props.onRetryFailedDraftClick}>
                <Trans id="sendfailed-retry">Retry</Trans>
              </MessageBannerAction>
              <MessageBannerAction onClick={this.toggleIsDeleteConfirmationOpen}>
                <Trans id="sendfailed-delete">Delete</Trans>
              </MessageBannerAction>
            </MessageBannerActionGroup>
          </MessageBanner>
        )}
      </UserThemeConsumer>
    );
  }

  private maybeRenderDeleteConfirmation() {
    if (!this.state.isDeleteConfirmationOpen) {
      return null;
    }

    return (
      <OkCancelDialog
        title={<Trans id="delete-message-title">Delete message</Trans>}
        okButtonLabel={<Trans id="delete-message-ok">Delete</Trans>}
        onOkClick={this.props.onDeleteDraftClick}
        onRequestClose={this.toggleIsDeleteConfirmationOpen}
      >
        <Trans id="delete-message-content">
          Would you like to delete this message?
          <br />
          This action cannot be undone.
        </Trans>
      </OkCancelDialog>
    );
  }

  private maybeRenderDraftError() {
    const {error} = this.props.message;

    // If server side error, display its description.
    if (error && error.source === MessageErrorSourcesEnum.SERVER && error.description) {
      return (
        <MessageBannerSubtitle>
          <DomMarkdownRenderer
            debugIdentifier="failed-message-banner"
            components={markdownDomComponents}
            text={error.description}
          />
        </MessageBannerSubtitle>
      );
    }

    // If network error while saving the draft, display the "offline" banner.
    if (error && error.type === MessageErrorsEnum.CANT_SAVE) {
      return (
        <MessageBannerSubtitle>
          <Trans id="sendfailed-cantsave">You might be offline.</Trans>
        </MessageBannerSubtitle>
      );
    }

    return null;
  }
}
